import { supabase } from '../lib/supabaseClient';

export type UserTheme = 'light' | 'midnight' | 'black';

const USER_SETTINGS_SCHEMA_CACHE_KEY = 'workflow_user_settings_schema_state';
const USER_SETTINGS_SCHEMA_TTL_MS = 6 * 60 * 60 * 1000;

let userSettingsTableAvailable: boolean | null = null;

const normalizeTheme = (value: unknown): UserTheme | null => {
  if (value === 'light' || value === 'midnight' || value === 'black') return value;
  return null;
};

const isMissingUserSettingsTableError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;

  const code = 'code' in error ? String(error.code ?? '') : '';
  const message = 'message' in error ? String(error.message ?? '') : '';

  return code === 'PGRST205' || message.includes("Could not find the table 'public.user_settings'");
};

const readCachedSchemaAvailability = (): boolean | null => {
  if (typeof window === 'undefined') return null;

  try {
    const rawValue = window.localStorage.getItem(USER_SETTINGS_SCHEMA_CACHE_KEY);
    if (!rawValue) return null;

    const parsed = JSON.parse(rawValue) as { available?: boolean; updatedAt?: number };
    const updatedAt = Number(parsed?.updatedAt ?? 0);

    if (!updatedAt || Date.now() - updatedAt > USER_SETTINGS_SCHEMA_TTL_MS) {
      window.localStorage.removeItem(USER_SETTINGS_SCHEMA_CACHE_KEY);
      return null;
    }

    return typeof parsed?.available === 'boolean' ? parsed.available : null;
  } catch {
    return null;
  }
};

const writeCachedSchemaAvailability = (available: boolean) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(
      USER_SETTINGS_SCHEMA_CACHE_KEY,
      JSON.stringify({ available, updatedAt: Date.now() }),
    );
  } catch {
    /* ignore */
  }
};

const getUserSettingsTableAvailability = (): boolean | null => {
  if (userSettingsTableAvailable !== null) {
    return userSettingsTableAvailable;
  }

  const cachedValue = readCachedSchemaAvailability();
  if (cachedValue !== null) {
    userSettingsTableAvailable = cachedValue;
  }

  return userSettingsTableAvailable;
};

const setUserSettingsTableAvailability = (available: boolean) => {
  userSettingsTableAvailable = available;
  writeCachedSchemaAvailability(available);
};

export async function getUserThemeSetting(userId: string, organizationId: string): Promise<UserTheme | null> {
  if (getUserSettingsTableAvailability() === false) {
    return null;
  }

  const { data, error } = await supabase
    .from('user_settings')
    .select('theme')
    .eq('user_id', userId)
    .eq('organization_id', organizationId)
    .maybeSingle();

  if (error) {
    if (isMissingUserSettingsTableError(error)) {
      setUserSettingsTableAvailability(false);
      return null;
    }
    throw error;
  }

  setUserSettingsTableAvailability(true);
  return normalizeTheme(data?.theme);
}

export async function upsertUserThemeSetting(params: {
  userId: string;
  organizationId: string;
  theme: UserTheme;
}): Promise<void> {
  if (getUserSettingsTableAvailability() === false) {
    return;
  }

  // 'black' is a client-side-only preference — DB only stores 'light'|'midnight'
  const dbTheme = params.theme === 'black' ? 'midnight' : params.theme;

  const { error } = await supabase
    .from('user_settings')
    .upsert(
      {
        user_id: params.userId,
        organization_id: params.organizationId,
        theme: dbTheme,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,organization_id' },
    );

  if (error) {
    if (isMissingUserSettingsTableError(error)) {
      setUserSettingsTableAvailability(false);
      return;
    }
    throw error;
  }

  setUserSettingsTableAvailability(true);
}
